import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import PropTypes from 'prop-types';

import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import CloseIcon from '@mui/icons-material/Close';
import { LinearProgress, styled, useTheme, TablePagination, IconButton } from "@mui/material";

import useDataSource from "../../../../hooks/useDataSource";

import { useLocation } from "react-router-dom";
import * as ParameterUtilities from "../../../../utilities/ParameterUtilities";
import {dataAddFilter, dataRemoveFilter, filterUpdateConditionAction} from "../../../../redux/data/dataActions";

const PREFIX = 'NavigationPager';

const classes = {
    root: `${PREFIX}-root`,
    toolbar: `${PREFIX}-toolbar`,
    spacer: `${PREFIX}-spacer`,
    pageNumContainer: `${PREFIX}-pageNumContainer`,
    currentPage: `${PREFIX}-currentPage`,
    linearProgress: `${PREFIX}-linearProgress`,
    actionStyles: `${PREFIX}-actionStyles`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')((
    {
        theme
    }
) => ({
    [`& .${classes.root}`]: {
        width: '90%',
        display: 'flex',
        justifyContent: 'center',
        maxHeight: '56px',
        padding: '0',
        overflow: 'hidden',
        borderBottom: 'none'
    },

    [`& .${classes.toolbar}`]: {
        display: 'flex',
        width: '100%',
        height: '100%',
        paddingLeft: '0px',
        paddingRight: '10px'
    },

    [`& .${classes.spacer}`]: {
        flex: 0
    },

    [`& .${classes.pageNumContainer}`]: {
        marginLeft: '8px',
        width: '46px',
        textAlign: 'center',
    },

    [`& .${classes.currentPage}`]: {
        width: '100%',
        height: 'calc(100% - 56px)',
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px',
        marginLeft: '4px',
        marginRight: '4px'
    },

    [`& .${classes.actionStyles}`]: {
        flexGrow: 2,
        display: 'flex',
        flexShrink: 0,
        color: theme.palette.text.secondary,
        marginLeft: '2px',
        marginRight: '2px'
    }
}));

// const useActionStyles = styled(theme => ({
//     root: {
//         flexGrow: 2,
//         display: 'flex',
//         flexShrink: 0,
//         color: theme.palette.text.secondary,
//         marginLeft: '2px',
//         marginRight: '2px'
//     },
// }));

export default function NavigationPager({configuration}) {

    const dispatch = useDispatch();

    const dataSourceName = configuration.dataSource?.name ? configuration.dataSource.name : '';
    const key = configuration.dataSource?.key ? configuration.dataSource.key : '';

    // App State
    const dataSource = useDataSource(dataSourceName);
    const activeRoute = useSelector(state => state.router.location.pathname);
    const filters = useSelector(state => state.data.filters);

    // Component State
    const [detailIndex, setDetailIndex] = useState(0);
    const [dataSourceInitialized, setDataSourceInitialized] = useState(false);
    const [outsideFilter, setOutsideFilter] = useState('');

    let location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    let id = searchParams.get('id');

    // Render current page
    useEffect(() => {

        // Ignore the change i.e. default of 0 until
        // we have data
        if (!dataSourceInitialized)
            return;

        // console.log('-- DETAIL INDEX --> {0}'.format(detailIndex));
        // TODO: We need to know here if we are hydrating a route
        // or NOT... if we are then we do not want to push another ID onto
        // the stack bc it causes a flicker

        if (dataSource.data) {
            // console.log('FETCHING->' + dataSource.fetching);
            if (
                dataSource.data.length > 0 &&
                detailIndex >= 0 &&
                detailIndex <= dataSource.data.length - 1
            ) {
                // Parse out the URL/Tab and then push on the id
                const currentRoute = ParameterUtilities.trimParams(activeRoute);
                // console.log('current route: ' + currentRoute);

                const row = dataSource.data[detailIndex];
                const newRoute = '{0}?id={1}'.format(currentRoute,row[key])
                // console.log('NavigationPager: pushing {0}'.format(newRoute));

                dispatch(push(newRoute));
                dispatch(dataRemoveFilter(outsideFilter));
            }
        }
    }, [detailIndex]);

    // Update detailIndex feature
    useEffect(() => {
        if (dataSource.data) {
            // Listen to pager changes
            setDataSourceInitialized(true);

            setTimeout(function(){
                if (dataSource.data.length > 0) {
                    const filter = filters.find(f => {
                        return f.id.toString().toLowerCase() !== 'navigation-gid' && f.field.toString().toLowerCase() === 'gid'
                    });

                    if (filter) {
                        let gidValues = filter.conditions[0].substring(
                            filter.conditions[0].lastIndexOf("(") + 1,
                            filter.conditions[0].lastIndexOf(")")
                        );
                        const values = gidValues.split(',').filter(p => p.length > 0);
                        const firstGID = values[0];
                        const row = dataSource.data.find(d => d.gid?.toString().toLowerCase() === firstGID?.toString().toLowerCase());
                        id = row.id;
                        setOutsideFilter(filter.id);
                    }

                    const rowIndex = dataSource.data.findIndex(d => d[key]?.toString().toLowerCase() === id?.toString().toLowerCase());
                    // Determine if we need to reindex based on the filtered data i.e.
                    // position could shift left/right
                    // console.log('Pager Feature: ------------->');
                    // console.log(rowIndex);
                    // console.log(dataSource.data);
                    // console.log(id);
                    // console.log('key', key);

                    // If outside filter selected itself again, remove the filter
                    if ((detailIndex === 0) || (detailIndex === rowIndex)) {
                        dispatch(dataRemoveFilter(outsideFilter));
                    }

                    if (rowIndex >= 0) {
                        // console.log('setDetailIndex(rowIndex:{0})'.format(rowIndex));
                        setDetailIndex(rowIndex);
                    } else {
                        // console.log('setDetailIndex(0)');
                        setDetailIndex(0);
                    }
                }
            }, 0);
        }
    }, [dataSource.data, filters]);

    function TablePaginationActions(props) {
        // const classes = useActionStyles();
        const theme = useTheme();
        const { count, page } = props;

        function handleFirstPageButtonClick(event) {

            setDetailIndex(0);

        }

        function handleBackButtonClick(event) {

            setDetailIndex(detailIndex - 1);

        }

        function handleNextButtonClick(event) {

            setDetailIndex(detailIndex + 1);

        }

        function handleLastPageButtonClick(event) {

            setDetailIndex(dataSource.data.length - 1);

        }

        function handleCloseClick(event) {

            // Parse current route i.e. no id and push
            const currentRoute = ParameterUtilities.trimParams(activeRoute);
            // console.log('handle close -> current route: ' + currentRoute);

            dispatch(push(currentRoute));
        }

        return (
            <div className={classes.actionStyles}>
                <IconButton
                    onClick={handleFirstPageButtonClick}
                    disabled={page === 0 || dataSource.data.length <= 1}
                    aria-label="first photo"
                    size="small"
                    style={{ marginLeft: 'auto', flex: '0 0 auto' }}
                >
                    {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
                </IconButton>
                <IconButton
                    onClick={handleBackButtonClick}
                    disabled={page === 0 || dataSource.data.length <= 1}
                    aria-label="previous photo"
                    size="small"
                    style={{ marginLeft: 'auto', flex: '0 0 auto' }}
                >
                    {theme.direction === 'rtl' ? (
                        <KeyboardArrowRight />
                    ) : (
                        <KeyboardArrowLeft />
                    )}
                </IconButton>
                <IconButton
                    onClick={handleNextButtonClick}
                    disabled={page >= count - 1 || dataSource.data.length <= 1}
                    aria-label="next photo"
                    size="small"
                    style={{ marginLeft: 'auto', flex: '0 0 auto' }}
                >
                    {theme.direction === 'rtl' ? (
                        <KeyboardArrowLeft />
                    ) : (
                        <KeyboardArrowRight />
                    )}
                </IconButton>
                <IconButton
                    onClick={handleLastPageButtonClick}
                    disabled={page >= count - 1 || dataSource.data.length <= 1}
                    aria-label="last photo"
                    size="small"
                    style={{ marginLeft: 'auto', flex: '0 0 auto' }}
                >
                    {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
                </IconButton>
                <IconButton
                    onClick={handleCloseClick}
                    aria-label="close"
                    size="small"
                    style={{ marginLeft: 'auto', flex: '0 0 auto' }}
                >
                    {theme.direction === 'rtl' ? <CloseIcon /> : <CloseIcon />}
                </IconButton>
            </div>
        );
    }

    TablePaginationActions.propTypes = {
        count: PropTypes.number.isRequired,
        onChangePage: PropTypes.func.isRequired,
        page: PropTypes.number.isRequired,
        rowsPerPage: PropTypes.number.isRequired,
    };

    return (
        <Root className={classes.root}
              style={{width: '100%',
                display: 'flex',
                justifyContent: 'center',
                maxHeight: '56px',
                padding: '0',
                overflow: 'hidden',
                borderBottom: 'none'}}>
            {dataSource.fetching &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {!dataSource.fetching &&
                <div className={classes.linearProgress}/>
            }
            {/*<Skeleton className={classes.root} variant="rect" />*/}
            <TablePagination
                className={classes.root}
                classes={{
                    root: classes.root,
                    toolbar: classes.toolbar,
                    spacer: classes.spacer
                }}
                rowsPerPageOptions={[]}
                colSpan={3}
                count={dataSource.data ? dataSource.data.length : 0}
                rowsPerPage={1}
                page={detailIndex}
                SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                }}
                // onChangePage={handleChangePage}
                labelDisplayedRows={({ from, to, count }) => {
                    return (
                        <div className={classes.pageNumContainer}>
                            {count > 0 ? '{0}-{1}'.format(to, count) : ''}
                        </div>
                    );
                }}
                ActionsComponent={TablePaginationActions}
            />
        </Root>
    );
}
